import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../config';
import LandingHeader from '../Shared/LandingHeader';
import LandingFooter from '../Shared/LandingFooter';
import './ContactUs.css';

const ContactUs = () => {
    const { t } = useTranslation(['contact', 'common']);
    
    const subjects = t('contact:form.subjects', { returnObjects: true }) || [];
    const contactInfoLabels = t('contact:info.items', { returnObjects: true }) || {};
    const contactInfoValues = t('contact:info.values', { returnObjects: true }) || {};

    const CONTACT_INFO = [
        {
            icon: '📧',
            label: contactInfoLabels.email,
            value: 'support@skilltherapyai.com',
            href: 'mailto:support@skilltherapyai.com',
        },
        {
            icon: '🕐',
            label: contactInfoLabels.hours,
            value: contactInfoValues.hours,
            href: null,
        },
        {
            icon: '📍',
            label: contactInfoLabels.office,
            value: contactInfoValues.office,
            href: null,
        },
        {
            icon: '⚖️',
            label: contactInfoLabels.legal,
            value: contactInfoValues.legal,
            href: null,
        },
    ];

    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    });
    const [status, setStatus] = useState('idle'); // idle | loading | success | error
    const [responseMsg, setResponseMsg] = useState('');

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setResponseMsg('');

        try {
            const res = await fetch(`${API_BASE_URL}contact.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(form),
            });
            const data = await res.json();

            if (data.status === 'success') {
                setStatus('success');
                setResponseMsg(data.message || t('contact:alerts.success'));
                setForm({ name: '', email: '', phone: '', subject: '', message: '' });
            } else {
                setStatus('error');
                setResponseMsg(data.message || t('contact:alerts.error'));
            }
        } catch {
            setStatus('error');
            setResponseMsg(t('contact:alerts.connection'));
        }
    };

    return (
        <div className="cu-page">
            <LandingHeader activePage="contact" />

            <main style={{ paddingTop: '80px' }}>
                {/* Hero */}
                <section className="cu-hero">
                    <div className="cu-container">
                        <div className="cu-hero-badge">{t('contact:hero.badge')}</div>
                        <h1>{t('contact:hero.title')} <span className="cu-gradient">{t('contact:hero.titleHighlight')}</span></h1>
                        <p className="cu-hero-sub">
                            {t('contact:hero.subtext')}
                        </p>
                    </div>
                    <div className="cu-hero-glow" />
                </section>

                {/* Main Grid */}
                <section className="cu-main-section">
                    <div className="cu-container cu-grid">

                        {/* Contact Form */}
                        <div className="cu-form-wrap">
                            <div className="cu-form-header">
                                <h2>{t('contact:form.title')}</h2>
                                <p>{t('contact:form.subtext')}</p>
                            </div>

                            {status === 'success' && (
                                <div className="cu-alert cu-alert--success">
                                    <span>✅</span>
                                    <p>{responseMsg}</p>
                                </div>
                            )}
                            {status === 'error' && (
                                <div className="cu-alert cu-alert--error">
                                    <span>⚠️</span>
                                    <p>{responseMsg}</p>
                                </div>
                            )}

                            <form className="cu-form" onSubmit={handleSubmit} noValidate>
                                <div className="cu-form-row">
                                    <div className="cu-field">
                                        <label htmlFor="cu-name">{t('contact:form.labels.name')} <span>{t('contact:form.labels.required')}</span></label>
                                        <input
                                            id="cu-name"
                                            type="text"
                                            name="name"
                                            placeholder={t('contact:form.placeholders.name')}
                                            value={form.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="cu-field">
                                        <label htmlFor="cu-email">{t('contact:form.labels.email')} <span>{t('contact:form.labels.required')}</span></label>
                                        <input
                                            id="cu-email"
                                            type="email"
                                            name="email"
                                            placeholder={t('contact:form.placeholders.email')}
                                            value={form.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="cu-form-row">
                                    <div className="cu-field">
                                        <label htmlFor="cu-phone">{t('contact:form.labels.phone')} <span className="optional">{t('contact:form.labels.optional')}</span></label>
                                        <input
                                            id="cu-phone"
                                            type="tel"
                                            name="phone"
                                            placeholder={t('contact:form.placeholders.phone')}
                                            value={form.phone}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="cu-field">
                                        <label htmlFor="cu-subject">{t('contact:form.labels.subject')} <span>{t('contact:form.labels.required')}</span></label>
                                        <select
                                            id="cu-subject"
                                            name="subject"
                                            value={form.subject}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">{t('contact:form.placeholders.subjectSelect')}</option>
                                            {subjects.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="cu-field cu-field--full">
                                    <label htmlFor="cu-message">{t('contact:form.labels.message')} <span>{t('contact:form.labels.required')}</span></label>
                                    <textarea
                                        id="cu-message"
                                        name="message"
                                        rows={6}
                                        placeholder={t('contact:form.placeholders.message')}
                                        value={form.message}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className={`cu-submit-btn ${status === 'loading' ? 'loading' : ''}`}
                                    disabled={status === 'loading'}
                                >
                                    {status === 'loading' ? (
                                        <><span className="cu-spinner" /> {t('contact:form.buttonSending')}</>
                                    ) : (
                                        <>{t('contact:form.button')}</>
                                    )}
                                </button>
                            </form>
                        </div>

                        {/* Info sidebar */}
                        <aside className="cu-info-sidebar">
                            <div className="cu-info-card">
                                <h3>{t('contact:info.title')}</h3>
                                <div className="cu-info-list">
                                    {CONTACT_INFO.map((item, i) => (
                                        <div className="cu-info-item" key={i}>
                                            <span className="cu-info-icon">{item.icon}</span>
                                            <div>
                                                <p className="cu-info-label">{item.label}</p>
                                                {item.href ? (
                                                    <a href={item.href} className="cu-info-value link">{item.value}</a>
                                                ) : (
                                                    <p className="cu-info-value">{item.value}</p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="cu-response-card">
                                <div className="cu-response-icon">⚡</div>
                                <h4>{t('contact:info.response.title')}</h4>
                                <p>{t('contact:info.response.text')}</p>
                            </div>

                            <div className="cu-quicklinks-card">
                                <h4>{t('contact:info.quicklinks.title')}</h4>
                                <ul>
                                    <li><Link to="/about">{t('common:about')}</Link></li>
                                    <li><Link to="/privacy">{t('common:privacy')}</Link></li>
                                    <li><Link to="/terms">{t('common:terms')}</Link></li>
                                    <li><Link to="/pricing">{t('common:pricing')}</Link></li>
                                </ul>
                            </div>
                        </aside>
                    </div>
                </section>
            </main>

            <LandingFooter />
        </div>
    );
};

export default ContactUs;
